import { useState } from 'react';
import { AlertTriangle, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { CollapsibleSection } from '@/components/collapsible-section';
import { useSubmission } from '@/lib/form-submissions';
import { isAdminSurface } from '@/lib/surface';
import { formatBytes, formatDateTime, formatPhone } from '@/lib/format';
import { safeHref, safeMailto, safeTel } from '@/lib/safe-url';
import type { FormSubmissionValueData } from '@/types/api';

export function SubmissionDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const submission = useSubmission(id);
  const data = submission.data;

  return (
    <Dialog open onClose={onClose} title={data?.name || data?.email || 'Submission'} size="lg">
      {submission.isLoading || !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-[var(--color-foreground-muted)]">
            {[data.form_name, data.client_name, formatDateTime(data.submitted_at)]
              .filter(Boolean)
              .join(' · ')}
          </p>

          {!data.is_valid && data.validation_errors ? (
            <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/5 px-3 py-2">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <AlertTriangle className="size-4 text-[var(--warning)]" />
                Some values did not match the field map
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-foreground-muted)]">
                {Object.entries(data.validation_errors).map(([key, messages]) => (
                  <li key={key}>
                    <span className="font-mono">{key}</span>: {(messages as string[]).join(' ')}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-[var(--color-foreground-subtle)]">
                It was kept anyway — a real enquiry is worth more than a schema match.
              </p>
            </div>
          ) : null}

          <dl className="space-y-3">
            {data.values.map((value) => (
              <ValueRow key={value.key} value={value} />
            ))}
          </dl>

          {data.attachments.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Attachments</p>
              <div className="space-y-1.5">
                {data.attachments.map((file) => (
                  <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40"
                  >
                    <Download className="size-4 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{file.file_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(file.file_size)}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ) : null}

          {isAdminSurface && data.unmapped && Object.keys(data.unmapped).length > 0 ? (
            <CollapsibleSection title={`Unmapped data (${Object.keys(data.unmapped).length})`}>
              <dl className="space-y-2">
                {Object.entries(data.unmapped).map(([key, value]) => (
                  <div key={key}>
                    <dt className="font-mono text-xs text-muted-foreground">{key}</dt>
                    <dd className="text-sm">{stringify(value)}</dd>
                  </div>
                ))}
              </dl>
            </CollapsibleSection>
          ) : null}

          {isAdminSurface && data.raw ? (
            <CollapsibleSection title="Raw payload">
              <pre className="max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs">
                {JSON.stringify(data.raw, null, 2)}
              </pre>
              {data.ip ? (
                <p className="mt-2 text-xs text-muted-foreground">Received from {data.ip}</p>
              ) : null}
            </CollapsibleSection>
          ) : null}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}

function ValueRow({ value }: { value: FormSubmissionValueData }) {
  const [showAll, setShowAll] = useState(false);

  if (value.value === null || value.value === '' ) {
    return null;
  }

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{value.label}</dt>
      <dd className="mt-0.5 text-sm">{render(value, showAll, () => setShowAll(true))}</dd>
    </div>
  );
}

function render(value: FormSubmissionValueData, showAll: boolean, onExpand: () => void) {
  const v = value.value;

  switch (value.type) {
    case 'email': {
      // Never linkify a value we could not vet — see lib/safe-url. Invalid
      // values still reach here because ingest stores them rather than
      // dropping a real enquiry.
      const mailto = safeMailto(v);
      return mailto ? (
        <a href={mailto} className="text-primary hover:underline">
          {String(v)}
        </a>
      ) : (
        <span className="break-words">{stringify(v)}</span>
      );
    }
    case 'phone': {
      const tel = safeTel(v);
      return tel ? (
        <a href={tel} className="text-primary hover:underline">
          {formatPhone(String(v))}
        </a>
      ) : (
        <span className="break-words">{stringify(v)}</span>
      );
    }
    case 'url':
    case 'file': {
      const href = safeHref(v);
      return href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 break-all text-primary hover:underline"
        >
          {href} <ExternalLink className="size-3" />
        </a>
      ) : (
        <span className="break-all">{stringify(v)}</span>
      );
    }
    case 'boolean':
      return <span>{v ? 'Yes' : 'No'}</span>;
    case 'multiselect':
      return (
        <div className="flex flex-wrap gap-1.5">
          {(Array.isArray(v) ? v : []).map((item) => (
            <span key={String(item)} className="rounded-full bg-muted px-2 py-0.5 text-xs">
              {String(item)}
            </span>
          ))}
        </div>
      );
    case 'json':
      return (
        <pre className="overflow-auto rounded-md border border-border bg-muted/30 p-2 font-mono text-xs">
          {JSON.stringify(v, null, 2)}
        </pre>
      );
    case 'textarea': {
      const text = String(v);
      const long = text.length > 600 && !showAll;
      return (
        <div className="whitespace-pre-wrap">
          {long ? text.slice(0, 600) : text}
          {long ? (
            <button type="button" onClick={onExpand} className="ml-1 text-primary hover:underline">
              show more
            </button>
          ) : null}
        </div>
      );
    }
    default:
      return <span className="break-words">{stringify(v)}</span>;
  }
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
